import {TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {get, getHeaders, post} from '@/services/infrastructure/api.service'
import {TransactionQueryParams} from '@/types/transaction/TransactionQueryParams'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {Report, ReportEntry} from '@/types/reports/Report'
import {Ledger} from '@/types/reports/Ledger'
import {Filters} from '@/query/Filters'
import {QueryFilter, QueryRequest} from '@/query/QueryRequest'
import {GeneralLedgerColumn} from '@/genleg/GeneralLedgerColumn'
import {GenLegQuery} from '@/genleg/GenLegQuery'

const GENERAL_LEDGER_PATH = '/reports/general-ledger'
const LEDGER_PATH = '/ledger'

const getSingleMovement = (entry: ReportEntry, toDate: string): number => {
    if (entry.groupedBy < '3000') {
        return entry.movements.reduce((acc, movement) => acc + movement.amount, 0)
    } else {
        return entry.movements
            .filter(movement => (new Date(movement.period)).getFullYear === (new Date(toDate)).getFullYear)
            .reduce((acc, movement) => acc + movement.amount, 0)
    }
}

const map = (report: Report): Ledger => {
    return {
        fromDate: report.from, toDate: report.to,
        ledgerAccounts: report.entries.toSorted((a, b) => Number.parseInt(a.groupedBy) - Number.parseInt(b.groupedBy)).map(entry => {
            return {
                accountNo: entry.groupedBy,
                incomingBalance: entry.incoming ?? 0,
                movement: getSingleMovement(entry, report.to),
                outgoingBalance: entry.outgoing ?? 0,
                numTransactionLines: entry.movements.reduce((acc, movement) => acc + (movement.count ?? 0), 0)
            }
        })
    }
}

const getGeneralLedgerByQuery = async (request: QueryRequest<GeneralLedgerColumn>, tenantId?: string): Promise<Ledger | undefined> => {
    const headers = await getHeaders(tenantId)
    const report = await post(GENERAL_LEDGER_PATH, request, headers) as Report | undefined
    if (report) return map(report)
    return undefined
}

const getGeneralLedger = async (query: GenLegQuery, tenantId?: string): Promise<Ledger | undefined> => {
    const filters: Partial<Record<GeneralLedgerColumn, QueryFilter>> = {
        date: Filters.and([
            Filters.date(query.fromDate).onOrAfter(),
            Filters.date(query.toDate).onOrBefore(),
        ]),
    }

    if (query.accountNo) {
        filters.accountNo = Filters.text(query.accountNo).equals()
    } else if (query.fromAccountNo || query.toAccountNo) {
        const conditions = []
        if (query.fromAccountNo) conditions.push(Filters.text(query.fromAccountNo).greaterThanOrEqualTo())
        if (query.toAccountNo) conditions.push(Filters.text(query.toAccountNo).lessThanOrEqualTo())
        filters.accountNo = Filters.and(conditions)
    }

    if (query.project) filters.project = Filters.text(query.project).equals()
    if (query.department) filters.department = Filters.text(query.department).equals()
    if (query.fullText) filters.fullText = Filters.text(query.fullText).equals()

    return getGeneralLedgerByQuery({filters, sorts: [], offset: 0, limit: 1000}, tenantId)
}

const getLedgerByDate = async (searchParams: TransactionSearchParams): Promise<Ledger | undefined> => {
    if (!searchParams.fromDate || !searchParams.toDate) return undefined
    return getGeneralLedger({
        fromDate: searchParams.fromDate,
        toDate: searchParams.toDate,
        department: searchParams.department,
        project: searchParams.project,
    })
}

const getAccountBalance = async (queryParams: TransactionQueryParams) => {
    const result = await get<Report>(`${LEDGER_PATH}/${queryParams.accountNo}/balance`, {
        fromDate: queryParams.fromDate,
        toDate: queryParams.toDate
    })
    return result?.entries[0]?.outgoing ?? 0
}

const sumLedgerRows = (rows: LedgerAccount[], key: keyof Omit<LedgerAccount, 'accountNo'>, initialValue?: number) => {
    if (!rows || rows.length === 0) initialValue ? initialValue : 0
    return rows.reduce((accumulator, currentValue) => accumulator + currentValue[key], initialValue ? initialValue : 0)
}

export {
    getLedgerByDate,
    getAccountBalance,
    sumLedgerRows,
    getGeneralLedger,
    getGeneralLedgerByQuery,
}
